import type { TaskPriority, TaskStatus } from "@sebas/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

export type LinkedNote = { id: string; title: string; color: string };

export type Task = {
  id: string;
  ownerId: string;
  teamId: string | null;
  title: string;
  description: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  dueAt: string | null;
  position: number;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  notes: LinkedNote[];
};

const KEY = ["tasks"] as const;

export function useTasks(teamId?: string) {
  return useQuery({
    queryKey: [...KEY, teamId ?? "all"],
    queryFn: () => api<{ tasks: Task[] }>(`/v1/tasks${teamId ? `?teamId=${teamId}` : ""}`),
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      title: string;
      status?: TaskStatus;
      priority?: TaskPriority;
      dueAt?: string | null;
      description?: string | null;
      teamId?: string | null;
    }) => api<{ task: Task }>("/v1/tasks", { method: "POST", body: JSON.stringify(input) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & Record<string, unknown>) =>
      api<{ task: Task }>(`/v1/tasks/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/**
 * Moves a card, optimistically.
 *
 * The board updates before the round trip so dragging feels immediate; the
 * snapshot is restored if the server rejects it. Neighbour ids are sent rather
 * than a computed position so the server owns the sort key — two people dragging
 * at once would otherwise be able to pick the same number.
 */
export function useMoveTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      status,
      afterId,
      beforeId,
    }: {
      id: string;
      status: TaskStatus;
      afterId?: string | null;
      beforeId?: string | null;
    }) =>
      api<{ task: Task }>(`/v1/tasks/${id}/move`, {
        method: "POST",
        body: JSON.stringify({ status, afterId: afterId ?? null, beforeId: beforeId ?? null }),
      }),
    onMutate: async ({ id, status }) => {
      await qc.cancelQueries({ queryKey: KEY });
      const previous = qc.getQueriesData<{ tasks: Task[] }>({ queryKey: KEY });
      for (const [key, data] of previous) {
        if (!data) continue;
        qc.setQueryData(key, {
          ...data,
          tasks: data.tasks.map((t) => (t.id === id ? { ...t, status } : t)),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      for (const [key, data] of ctx?.previous ?? []) qc.setQueryData(key, data);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/v1/tasks/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useLinkTaskNotes(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (noteIds: string[]) =>
      api<{ notes: LinkedNote[] }>(`/v1/tasks/${taskId}/notes`, {
        method: "PUT",
        body: JSON.stringify({ noteIds }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
